// Direct gateway to the Lyve AIA JamBase proxy (api.lyveaia.com).
// Auth is a single "X-JamBase-Key" header for every /jambase/* route
// (artists and events/festivals alike). Mirrors the reference Base44
// functions lyveJambaseArtists / lyveDmsEvents / lyveDmsFestivals /
// lyveDmsFestival, including their transform shapes.

import { proxifyImage } from './imageProxy';

const BASE = process.env.REACT_APP_LYVE_DMS_API_URL || 'https://api.lyveaia.com';
const KEY = process.env.REACT_APP_JAMBASE_API_KEY;

function headers() {
  return {
    accept: '*/*',
    'X-JamBase-Key': KEY || '',
  };
}

async function getJson(path) {
  const r = await fetch(`${BASE}${path}`, { headers: headers() });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`Lyve gateway ${r.status}: ${text.slice(0, 220)}`);
  }
  return r.json();
}

// ── helpers ──
function findLink(sameAs, id) {
  const arr = Array.isArray(sameAs) ? sameAs : [];
  return (arr.find((s) => s && s.identifier === id) || {}).url || '';
}

const cityOf = (loc) => (loc && loc.address ? loc.address.addressLocality : '') || '';
const countryOf = (loc) =>
  (loc && loc.address && loc.address.addressCountry ? loc.address.addressCountry.name : '') || '';
const isoDate = (s) => (s || '').slice(0, 10) || '—';
const performerNames = (performer) => (performer || []).map((p) => p.name);
const pickHeadliner = (performer) =>
  (performer || []).find((p) => p['x-isHeadliner']) || (performer || [])[0] || null;

function transformArtist(a) {
  if (!a) return null;
  const sameAs = Array.isArray(a.sameAs) ? a.sameAs : [];
  const members = Array.isArray(a.member) ? a.member.map((m) => m.name).filter(Boolean) : [];
  const loc =
    a.foundingLocation && typeof a.foundingLocation === 'object' ? a.foundingLocation.name || '' : '';
  return {
    name: a.name || '',
    identifier: a.identifier || '',
    url: a.url || '',
    image: proxifyImage(a.image || ''),
    kind: a['x-bandOrMusician'] || 'band',
    upcoming: Number(a['x-numUpcomingEvents'] || 0),
    genres: Array.isArray(a.genre) ? a.genre : [],
    spotify: findLink(sameAs, 'spotify'),
    officialSite: findLink(sameAs, 'officialSite'),
    musicbrainz: findLink(sameAs, 'musicbrainz'),
    facebook: findLink(sameAs, 'facebook'),
    twitter: findLink(sameAs, 'twitter'),
    members,
    foundingDate: a.foundingDate || '',
    foundingLocation: loc,
    upcomingEvents: Array.isArray(a.events)
      ? a.events.map((e) => ({
          name: e.name || '',
          startDate: (e.startDate || '').slice(0, 10),
          venue: (e.location && e.location.name) || '',
          city: (e.location && e.location.address && e.location.address.addressLocality) || '',
          country:
            (e.location &&
              e.location.address &&
              e.location.address.addressCountry &&
              e.location.address.addressCountry.name) ||
            '',
          url: e.url || '',
          status: e.eventStatus || '',
          performers: Array.isArray(e.performer)
            ? e.performer.map((p) => ({ name: p.name || '', headliner: !!p['x-isHeadliner'] }))
            : [],
          tickets: Array.isArray(e.offers)
            ? e.offers.map((o) => ({ name: o.name || '', url: o.url || '', category: o.category || '' }))
            : [],
        }))
      : [],
    upcomingStreams: Array.isArray(a.upcomingStreams)
      ? a.upcomingStreams.map((s) => ({
          name: s.name || '',
          url: s.url || '',
          startDate: (s.startDate || '').slice(0, 10),
        }))
      : [],
    externalIdentifiers: Array.isArray(a['x-externalIdentifiers'])
      ? a['x-externalIdentifiers'].map((x) => ({
          source: x.source || '',
          ids: Array.isArray(x.identifier) ? x.identifier.map(String) : [String(x.identifier || '')],
        }))
      : [],
    sameAs: sameAs.map((s) => ({ id: s.identifier || '', url: s.url || '', name: s.name || '' })),
  };
}

// ── festival transform (port of shared/festivalTransform.ts) ──
const genreSlug = (g) => (g ? String(g).replace(/_/g, ' ').toLowerCase() : '');

function buildAddress(loc) {
  const a = (loc && loc.address) || {};
  const norm = (v) => {
    if (v == null) return '';
    if (typeof v === 'object') return v.name || '';
    return String(v);
  };
  const parts = [
    norm(a.streetAddress),
    norm(a.addressLocality),
    a.postalCode,
    norm(a.addressRegion),
    norm(a.addressCountry),
  ].filter(Boolean);
  return parts.join(', ');
}

function coordsOf(loc) {
  const g = loc && loc.geo;
  if (!g) return '';
  const lat = g.latitude != null ? Number(g.latitude) : null;
  const lon = g.longitude != null ? Number(g.longitude) : null;
  if (lat == null || lon == null) return '';
  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
}

function modeOf(e) {
  const m = (e.eventAttendanceMode || '').toLowerCase();
  if (m === 'live' || /offline/i.test(m)) return 'Live';
  if (m === 'online' || /online/i.test(m)) return 'Online';
  if (m === 'mixed' || /mixed/i.test(m)) return 'Hybrid';
  return '';
}

function fmtPrice(o) {
  const p = o.priceSpecification || {};
  const cur = (p.priceCurrency || '').trim();
  if (p.minPrice != null && p.maxPrice != null) return `${p.minPrice}–${p.maxPrice} ${cur}`.trim();
  if (p.price != null) return `${p.price} ${cur}`.trim();
  return '';
}

function transformOffers(offers) {
  return (offers || [])
    .filter((o) => o && (o.url || o.name))
    .map((o) => ({
      name: o.name || (o.category === 'ticketingLinkPrimary' ? 'Primary' : 'Tickets'),
      url: o.url || '',
      category: o.category || '',
      price: fmtPrice(o),
      availability: o.availability || '',
      validFrom: o.validFrom || '',
    }));
}

function transformFestival(e) {
  if (!e) return null;
  const loc = e.location || {};
  const addr = loc.address || {};
  const city = cityOf(loc);
  const country = countryOf(loc);
  const venueName = loc.name || '—';
  const perf = performerNames(e.performer);
  const start = isoDate(e.startDate);
  const end = isoDate(e.endDate);
  const status = e.eventStatus === 'scheduled' ? 'confirmed' : 'unconfirmed';
  const headliner = pickHeadliner(e.performer);
  const client = headliner ? headliner.name : perf[0] || '—';
  const img = e.image || loc.image || (headliner && headliner.image) || '';
  const performers = (e.performer || [])
    .filter((p) => p && p.name)
    .map((p) => ({
      name: p.name,
      url: p.url || p['@id'] || '',
      headliner: !!p['x-isHeadliner'],
      genre: genreSlug(p['x-genre'] || p.genre || ''),
    }));
  const offers = transformOffers(e.offers);
  const primary = offers.find((o) => /primary/i.test(o.category)) || offers[0] || null;
  const organizer = e.organizer
    ? Array.isArray(e.organizer)
      ? e.organizer.filter((o) => o && (o.name || o.url)).map((o) => ({ name: o.name || '', url: o.url || '' }))
      : [{ name: e.organizer.name || '', url: e.organizer.url || '' }]
    : [];
  const sponsors = (e.sponsor || [])
    .filter((s) => s && (s.name || s.url))
    .map((s) => ({ name: s.name || '', url: s.url || '' }));
  return {
    id: e.identifier || e.id || '',
    n: e.name,
    role: e['@type'] || 'Event',
    type: (e['@type'] || 'event').toLowerCase(),
    iso: start || '2027-01-01',
    end,
    eventStatus: e.eventStatus || 'scheduled',
    status,
    client,
    img: proxifyImage(img),
    venue: `${venueName}, ${city}`,
    venueName,
    venueUrl: loc.url || '',
    venueId: loc.identifier || '',
    meta: `${city} · ${country}`,
    timezone: addr['x-timezone'] || e.timezone || '',
    mode: modeOf(e),
    address: buildAddress(loc),
    postalCode: addr.postalCode || '',
    coords: coordsOf(loc),
    maxCapacity: loc.maximumAttendeeCapacity || '',
    permanentlyClosed: !!loc['x-isPermanentlyClosed'],
    numUpcoming: loc['x-numUpcomingEvents'] != null ? loc['x-numUpcomingEvents'] : '',
    doorTime: e.doorTime || '',
    previousStartDate: e.previousStartDate || '',
    datePublished: e.datePublished || '',
    dateModified: e.dateModified || '',
    description: e.description || '',
    organizer,
    sponsors,
    performers,
    offers,
    ticketName: primary ? primary.name : '',
    ticketUrl: primary ? primary.url : '',
    isFree: e.isAccessibleForFree === true,
    sameAs: (Array.isArray(e.sameAs) ? e.sameAs : e.sameAs ? [e.sameAs] : [])
      .map((s) =>
        s && typeof s === 'object' ? s.url || s['@id'] || '' : typeof s === 'string' ? s : ''
      )
      .filter(Boolean),
    jambaseUrl: e.url || '',
    about: `${e.name} — festival at ${venueName}. ${start ? 'Date ' + start + '. ' : ''}${
      e.endDate ? 'Through ' + end + '. ' : ''
    }${perf.length ? 'Line-up: ' + perf.join(', ') + '.' : ''}`.trim(),
    tags: [country].filter(Boolean),
    line: perf,
    kv: [
      ['Type', 'Festival'],
      ['Date', start || '—'],
      ['End date', end],
      ['Venue', venueName],
      ['City', city],
      ['Country', country],
      ['Line-up', perf.join(', ') || '—'],
      ['Lyve DMS', e.url || '—'],
    ],
  };
}

// ── endpoint functions ──
async function jambaseArtists(payload = {}) {
  if (payload.identifier) {
    const id = String(payload.identifier).trim();
    const expand =
      'expandUpcomingEvents=true&expandUpcomingStreams=true&expandExternalIdentifiers=true&expandArtistSameAs=true';
    const j = await getJson(`/jambase/artists/${encodeURIComponent(id)}?${expand}`);
    const a = j.artist || (j && j.name ? j : j.data) || j;
    return { artist: transformArtist(a) };
  }
  const page = Math.min(Math.max(Number(payload.page) || 1, 1), 500);
  const perPage = Math.min(Math.max(Number(payload.perPage) || 40, 1), 100);
  const artistName = String(payload.artistName || '').trim();
  const params = new URLSearchParams();
  if (artistName) params.set('artistName', artistName);
  if (payload.genreSlug) params.set('genreSlug', String(payload.genreSlug).trim());
  if (payload.artistHasUpcomingEvents) params.set('artistHasUpcomingEvents', String(payload.artistHasUpcomingEvents));
  params.set('page', String(page));
  params.set('perPage', String(perPage));
  const j = await getJson(`/jambase/artists?${params.toString()}`);
  const raw = Array.isArray(j.artists) ? j.artists : Array.isArray(j.results) ? j.results : Array.isArray(j.items) ? j.items : [];
  const artists = raw.map(transformArtist).filter(Boolean);
  const p = j.pagination || {};
  const totalPages = Number(p.totalPages || 1);
  const curPage = Number(p.page || page);
  return {
    artists,
    page: curPage,
    perPage: Number(p.perPage || perPage),
    totalItems: Number(p.totalItems || artists.length),
    totalPages,
    hasMore: curPage < totalPages,
  };
}

// JamBase /jambase/events query params. The gateway forwards them verbatim.
const EVENT_FILTERS = [
  'name',
  'artistName',
  'venueName',
  'geoCityName',
  'genreSlug',
  'eventType',
  'geoCountryIso2',
  'geoStateIso',
  'geoMetroId',
  'eventDatePreset',
  'eventDateFrom',
  'eventDateTo',
];

function buildEventParams(payload, defaults) {
  const params = new URLSearchParams(defaults);
  EVENT_FILTERS.forEach((k) => {
    const v = payload[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') params.set(k, String(v).trim());
  });
  return params;
}

async function dmsEvents(payload = {}) {
  const perPage = Math.min(Math.max(Number(payload.perPage) || 50, 1), 100);
  const page = Math.min(Math.max(Number(payload.page) || 1, 1), 500);
  const params = buildEventParams(
    { name: payload.q || payload.name, ...payload },
    { page: String(page), perPage: String(perPage) }
  );
  const j = await getJson(`/jambase/events?${params.toString()}`);
  const events = Array.isArray(j.events) ? j.events.map(transformFestival).filter(Boolean) : [];
  const p = j.pagination || {};
  return {
    events,
    page: Number(p.page || page),
    perPage: Number(p.perPage || perPage),
    totalItems: Number(p.totalItems || events.length),
    totalPages: Number(p.totalPages || 1),
    hasMore: (Number(p.page || page) < Number(p.totalPages || 1)),
    source: 'Lyve DMS',
  };
}

async function dmsFestivals(payload = {}) {
  const perPage = Math.min(Math.max(Number(payload.perPage) || 50, 1), 100);
  const params = buildEventParams(
    { name: payload.q || payload.name, ...payload, eventType: 'festivals' },
    { page: '1', perPage: String(perPage) }
  );
  const j = await getJson(`/jambase/events?${params.toString()}`);
  const festivals = Array.isArray(j.events) ? j.events.map(transformFestival).filter(Boolean) : [];
  return { festivals, source: 'Lyve DMS' };
}

async function dmsFestival(payload = {}) {
  const id = String(payload.id || '').trim();
  if (!id) return { festival: null };
  const j = await getJson(`/jambase/events/${encodeURIComponent(id)}`);
  const event = j.event || (Array.isArray(j.events) ? j.events[0] : null) || j;
  return { festival: transformFestival(event) };
}

// ── network directory (LYVE AIA live-entertainment network) ──
// /directory/v1/{entity} lists summary records; /directory/v1/{entity}/{id}
// returns the full record. No auth required (public GET).
async function directory(payload = {}) {
  const entity = String(payload.entity || '').trim();
  if (!entity) return { error: 'Missing directory entity' };
  const id = payload.id != null && payload.id !== '' ? String(payload.id).trim() : '';
  if (id) {
    const j = await getJson(`/directory/v1/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`);
    return { entity, item: j };
  }
  const limit = Math.min(Math.max(Number(payload.limit) || 200, 1), 500);
  const offset = Math.max(Number(payload.offset) || 0, 0);
  const j = await getJson(`/directory/v1/${encodeURIComponent(entity)}?limit=${limit}&offset=${offset}`);
  const data = Array.isArray(j.data) ? j.data : [];
  return {
    entity,
    items: data,
    total: Number(j.total || data.length),
    count: Number(j.count || data.length),
    label: j.label || '',
  };
}

// ── geographies (JamBase /geographies/{countries|states|metros|cities}) ──
async function geographies(payload = {}) {
  const kind = String(payload.kind || '').trim();
  if (!kind) return { error: 'Missing geography kind' };
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(payload)) {
    if (k === 'kind') continue;
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJson(`/jambase/geographies/${kind}?${params.toString()}`);
}

// ── streams (JamBase /streams — livestreams) ──
async function streams(payload = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(payload || {})) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return getJson(`/jambase/streams?${params.toString()}`);
}

export const lyveGateway = {
  lyveJambaseArtists: jambaseArtists,
  lyveDmsEvents: dmsEvents,
  lyveDmsFestivals: dmsFestivals,
  lyveDmsFestival: dmsFestival,
  lyveDirectory: directory,
  lyveGeographies: geographies,
  lyveStreams: streams,
};

export default lyveGateway;
